/* eslint-disable max-lines */
import { WALLET_TYPES, STABLECOIN, BankAccount, CURRENCY, BANK_TRANSFER_NETWORK } from '~/types'
import { BlockchainAddress } from '~/biz/store/blockchainAddresses/types'
import { ACCEPTED_CURRENCY } from '~/biz/types'

export type AccountLimits = {
  withdrawDailyRemaining: string
  withdrawDailyMax: string
}

export type MerchantAccount = {
  digitalGoodsWallet: {
    availableBalance: string
    fundPoolId?: number
    accountLimit: AccountLimits
  } | null
  hasBusinessAccount?: boolean
}

export type DashboardBanner = {
  title: string
  tag: string
  content: string
}

export type StablecoinFee = {
  amount: number | undefined
  identifier: string | undefined
  timestamp: string | undefined
}

export type FiatFee = {
  amount: number | undefined
  feeType: FeeType | undefined
  rate: number | undefined
  maxAmount: number | undefined
  minAmount: number | undefined
  threshold: number | undefined
}

export enum TRANSFER_METHOD {
  // eslint-disable-next-line @typescript-eslint/no-shadow
  STABLECOIN = 'stablecoin',
  BANK = 'bank',
}

export type Blockchain =
  | 'zilliqa'
  | 'ethereum'
  | 'hedera'
  | 'polygon'
  | 'avalanche'
  | 'tron'
  | 'bnbsmart'
  | 'arbitrum'
  | 'ripple'
  | 'base'

export type WithdrawWalletIdType = WALLET_TYPES.DIGITAL_GOODS | undefined
export type FeeType = 'PercentageFeeInstruction' | 'FixedFeeInstruction'

export type WithdrawLimits = {
  [key in ACCEPTED_CURRENCY]: {
    FIAT_MIN: Record<string, number>
    FIAT_MAX: Record<string, number>
    STABLECOIN_MAX: number
    STABLECOIN_MIN: number
    STABLECOIN_UNAPPROVED_MAX: number
  }
}

export enum STRAITSX_LANDING_PAGE_ACTIONS {
  SET_LATEST_BUSINESS_DEPOSIT_REQUESTS = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_LATEST_BUSINESS_DEPOSIT_REQUESTS',
  SET_IS_ZRC2_DEPOSIT_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_ZRC2_DEPOSIT_ENABLED',
  SET_IS_ERC20_DEPOSIT_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_ERC20_DEPOSIT_ENABLED',
  SET_IS_HTS_DEPOSIT_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_HTS_DEPOSIT_ENABLED',
  SET_IS_MATIC_DEPOSIT_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_MATIC_DEPOSIT_ENABLED',
  SET_IS_AVAX_DEPOSIT_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_AVAX_DEPOSIT_ENABLED',
  SET_IS_TRC20_DEPOSIT_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_TRC20_DEPOSIT_ENABLED',
  SET_IS_BEP20_DEPOSIT_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_BEP20_DEPOSIT_ENABLED',
  SET_IS_ARB_DEPOSIT_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_ARB_DEPOSIT_ENABLED',
  SET_IS_XRP_DEPOSIT_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_XRP_DEPOSIT_ENABLED',
  SET_IS_ZRC2_WITHDRAWAL_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_ZRC2_WITHDRAWAL_ENABLED',
  SET_IS_ERC20_WITHDRAWAL_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_ERC20_WITHDRAWAL_ENABLED',
  SET_IS_HTS_WITHDRAWAL_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_HTS_WITHDRAWAL_ENABLED',
  SET_IS_MATIC_WITHDRAWAL_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_MATIC_WITHDRAWAL_ENABLED',
  SET_IS_AVAX_WITHDRAWAL_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_AVAX_WITHDRAWAL_ENABLED',
  SET_IS_TRC20_WITHDRAWAL_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_TRC20_WITHDRAWAL_ENABLED',
  SET_IS_BEP20_WITHDRAWAL_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_BEP20_WITHDRAWAL_ENABLED',
  SET_IS_ARB_WITHDRAWAL_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_ARB_WITHDRAWAL_ENABLED',
  SET_IS_XRP_WITHDRAWAL_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_XRP_WITHDRAWAL_ENABLED',
  SET_IS_DASHBOARD_BANNER_MANAGEMENT_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_DASHBOARD_BANNER_MANAGEMENT_ENABLED',
  SET_IS_ZRC2_NOTICE_BANNER_VISIBLE = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_ZRC2_NOTICE_BANNER_VISIBLE',
  SET_DASHBOARD_BANNERS = 'ROUTE_ACTIONS.SET_DASHBOARD_BANNERS',
  SET_TAB = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_TAB',
  SET_TRANSFER_METHOD = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_TRANSFER_METHOD',
  SET_SELECTED_CURRENCY = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_SELECTED_CURRENCY',
  SET_SELECTED_BLOCKCHAIN = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_SELECTED_BLOCKCHAIN',
  SET_SELECTED_BLOCKCHAIN_ADDRESS = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_SELECTED_BLOCKCHAIN_ADDRESS',
  SET_BANK_TRANSFER_NETWORK = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_BANK_TRANSFER_NETWORK',
  SET_SELECTED_BANK_ACCOUNT = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_SELECTED_BANK_ACCOUNT',
  SET_BANK_ROUTING_CODE = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_BANK_ROUTING_CODE',
  SET_BANK_INTERMEDIARY_SWIFT_CODE = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_BANK_INTERMEDIARY_SWIFT_CODE',
  SET_WITHDRAW_AMOUNT = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_WITHDRAW_AMOUNT',
  SET_TRANSFER_REMARK = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_TRANSFER_REMARK',
  SET_IS_STABLECOIN_FEE_LOADING = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_STABLECOIN_FEE_LOADING',
  SET_STABLECOIN_FEE = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_STABLECOIN_FEE',
  SET_IS_FIAT_FEE_LOADING = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_FIAT_FEE_LOADING',
  SET_FIAT_FEE = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_FIAT_FEE',
  SET_IS_HTS_WITHDRAWAL_FEE_CHARGING = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_HTS_WITHDRAWAL_FEE_CHARGING',
  SET_IS_WITHDRAWAL_AUTOMATIC = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_WITHDRAWAL_AUTOMATIC',
  SET_CONFIRM_MODAL_OPEN = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_CONFIRM_MODAL_OPEN',
  SET_2_FA_MODAL_OPEN = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_2_FA_MODAL_OPEN',
  CREATE_WITHDRAWAL_REQUEST = 'STRAITSX_LANDING_PAGE_ACTIONS.CREATE_WITHDRAWAL_REQUEST',
  SET_WITHDRAW_WALLET_ID = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_WITHDRAW_WALLET_ID',
  SET_VISITED_GETTING_STARTED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_VISITED_GETTING_STARTED',
  SET_CURRENT_MERCHANT_ACCOUNT_ID = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_CURRENT_MERCHANT_ACCOUNT_ID',
  SET_IS_HEDERA_UI_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_HEDERA_UI_ENABLED',
  SET_IS_POLYGON_UI_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_POLYGON_UI_ENABLED',
  SET_IS_AVALANCHE_UI_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_AVALANCHE_UI_ENABLED',
  SET_IS_TRON_UI_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_TRON_UI_ENABLED',
  SET_IS_BNBSMART_UI_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_BNBSMART_UI_ENABLED',
  SET_IS_USDC_TRANSFER_UI_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_USDC_TRANSFER_UI_ENABLED',
  SET_IS_ARBITRUM_UI_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_ARBITRUM_UI_ENABLED',
  SET_IS_RIPPLE_UI_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_RIPPLE_UI_ENABLED',
  SET_HAS_FETCHING_FEE_ERROR = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_HAS_FETCHING_FEE_ERROR ',
  SET_IS_SSO_TWO_FA_AUTHENTICATED_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_SSO_TWO_FA_AUTHENTICATED_ENABLED ',
  SET_IS_DPT_RISK_MODAL_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_DPT_RISK_MODAL_ENABLED',
  SET_IS_FAZZ_VA_FOR_DASHBOARD_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_FLAZZ_VA_FOR_DASHBOARD_ENABLED',
  SET_IS_TRAVEL_RULE_DEPOSIT_DECLARATION_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_TRAVEL_RULE_DEPOSIT_DECLARATION_ENABLED',
  SET_IS_TRAVEL_RULE_ADDR_WHITELISTING_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_TRAVEL_RULE_ADDR_WHITELISTING_ENABLED',
  SET_IS_RENEW_ADDRESS_ACKNOWLEDGED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_RENEW_ADDRESS_ACKNOWLEDGED',
  SET_IS_XUSD_COACHMARK_ACTIVE = 'LANDING_PAGE_ACTIONS.SET_IS_XUSD_COACHMARK_ACTIVE',
  SET_IS_USDC_MATIC_DEPOSIT_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_USDC_MATIC_DEPOSIT_ENABLED',
  SET_IS_USDC_MATIC_WITHDRAWAL_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_USDC_MATIC_WITHDRAWAL_ENABLED',
  SET_IS_DASHBOARD_APPROVAL_FLOW_UI_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_DASHBOARD_APPROVAL_FLOW_UI_ENABLED',
  SET_IS_XUSD_ERC20_DEPOSIT_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_XUSD_ERC20_DEPOSIT_ENABLED',
  SET_IS_XUSD_ERC20_WITHDRAWAL_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_XUSD_ERC20_WITHDRAWAL_ENABLED',
  SET_IS_USDT_BEP20_DEPOSIT_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_USDT_BEP20_DEPOSIT_ENABLED',
  SET_IS_USDT_BEP20_WITHDRAWAL_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_USDT_BEP20_WITHDRAWAL_ENABLED',
  SET_IS_USDT_BEP20_UI_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_USDT_BEP20_UI_ENABLED',
  SET_IS_XSGD_SPL_UI_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_XSGD_SPL_UI_ENABLED',
  SET_IS_XUSD_SPL_UI_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_XUSD_SPL_UI_ENABLED',
  SET_IS_SOLANA_UI_ENABLED = 'STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_SOLANA_UI_ENABLED',
}

type SetIsZrc2DepositEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_ZRC2_DEPOSIT_ENABLED
  isXsgdZrc2DepositEnabled: boolean
}

type SetIsXsgdErc20DepositEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_ERC20_DEPOSIT_ENABLED
  isXsgdErc20DepositEnabled: boolean
}

type SetIsXsgdHtsDepositEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_HTS_DEPOSIT_ENABLED
  isXsgdHtsDepositEnabled: boolean
}

type SetIsXsgdMaticDepositEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_MATIC_DEPOSIT_ENABLED
  isXsgdMaticDepositEnabled: boolean
}

type SetIsXsgdAvaxDepositEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_AVAX_DEPOSIT_ENABLED
  isXsgdAvaxDepositEnabled: boolean
}

type SetIsXsgdArbDepositEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_ARB_DEPOSIT_ENABLED
  isXsgdArbDepositEnabled: boolean
}

type SetIsXsgdXrpDepositEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_XRP_DEPOSIT_ENABLED
  isXsgdXrpDepositEnabled: boolean
}

type SetIsUsdtTrc20DepositEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_TRC20_DEPOSIT_ENABLED
  isUsdtTrc20DepositEnabled: boolean
}

type SetIsXusdBep20DepositEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_BEP20_DEPOSIT_ENABLED
  isXusdBep20DepositEnabled: boolean
}

type SetIsXsgdZrc2WithdrawalEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_ZRC2_WITHDRAWAL_ENABLED
  isXsgdZrc2WithdrawalEnabled: boolean
}

type SetIsXsgdErc20WithdrawalEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_ERC20_WITHDRAWAL_ENABLED
  isXsgdErc20WithdrawalEnabled: boolean
}

type SetIsXsgdHtsWithdrawalEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_HTS_WITHDRAWAL_ENABLED
  isXsgdHtsWithdrawalEnabled: boolean
}

type SetIsXsgdMaticWithdrawalEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_MATIC_WITHDRAWAL_ENABLED
  isXsgdMaticWithdrawalEnabled: boolean
}

type SetIsXsgdAvaxWithdrawalEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_AVAX_WITHDRAWAL_ENABLED
  isXsgdAvaxWithdrawalEnabled: boolean
}

type SetIsXsgdArbWithdrawalEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_ARB_WITHDRAWAL_ENABLED
  isXsgdArbWithdrawalEnabled: boolean
}

type SetIsXsgdXrpWithdrawalEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_XRP_WITHDRAWAL_ENABLED
  isXsgdXrpWithdrawalEnabled: boolean
}

type SetIsUsdtTrc20WithdrawalEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_TRC20_WITHDRAWAL_ENABLED
  isUsdtTrc20WithdrawalEnabled: boolean
}

type SetIsXusdBep20WithdrawalEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_BEP20_WITHDRAWAL_ENABLED
  isXusdBep20WithdrawalEnabled: boolean
}

// USDC MATIC
type SetIsUsdcMaticDepositEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_USDC_MATIC_DEPOSIT_ENABLED
  isUsdcMaticDepositEnabled: boolean
}
type SetIsUsdcMaticWithdrawalEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_USDC_MATIC_WITHDRAWAL_ENABLED
  isUsdcMaticWithdrawalEnabled: boolean
}

type SetIsZrc2NoticeBannerEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_ZRC2_NOTICE_BANNER_VISIBLE
  isZrc2NoticeBannerVisible: boolean
}

type SetIsDashboardBannerManagementEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_DASHBOARD_BANNER_MANAGEMENT_ENABLED
  isDashboardBannerManagementEnabled: boolean
}

type SetDashboardBanners = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_DASHBOARD_BANNERS
  dashboardBanners: DashboardBanner[]
}

type SetTab = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_TAB
  tab: string
}

type SetTransferMethod = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_TRANSFER_METHOD
  transferMethod: TRANSFER_METHOD | null
}

type SetSelectedCurrency = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_SELECTED_CURRENCY
  selectedCurrency: STABLECOIN | null
}

type SetSelectedBlockchain = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_SELECTED_BLOCKCHAIN
  selectedBlockchain: Blockchain | null
}

type SetSelectedBlockchainAddress = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_SELECTED_BLOCKCHAIN_ADDRESS
  selectedBlockchainAddress: BlockchainAddress | null
}

type SetSelectedBankAccount = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_SELECTED_BANK_ACCOUNT
  selectedBankAccount: BankAccount | null
}

type SetBankTransferNetwork = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_BANK_TRANSFER_NETWORK
  bankTransferNetwork: BANK_TRANSFER_NETWORK | null
}

type SetBankRoutingCode = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_BANK_ROUTING_CODE
  bankRoutingCode: string | null
}

type SetBankIntermediarySwiftCode = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_BANK_INTERMEDIARY_SWIFT_CODE
  bankIntermediarySwiftCode: string | null
}

type SetWithdrawAmount = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_WITHDRAW_AMOUNT
  amount: string
  withdrawAmountError: string
}

type SetTransferRemark = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_TRANSFER_REMARK
  transferRemark: string | null
}

type SetWithdrawWalletId = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_WITHDRAW_WALLET_ID
  withdrawWalletId: WithdrawWalletIdType
}

type SetIsStablecoinFeeLoading = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_STABLECOIN_FEE_LOADING
  isStablecoinFeeLoading: boolean
}

type SetStablecoinFee = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_STABLECOIN_FEE
  amount: number
  identifier: string
  timestamp: string
}

type SetFiatFee = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_FIAT_FEE
  stablecoin: CURRENCY
  amount: number
  feeType: FeeType
  rate: number
  maxAmount: number
  minAmount: number
  threshold: number
}

type SetIsHtsWithdrawalFeeCharging = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_HTS_WITHDRAWAL_FEE_CHARGING
  isHtsWithdrawalFeeCharging: boolean
}

type SetIsWithdrawalAutomatic = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_WITHDRAWAL_AUTOMATIC
  isWithdrawalAutomatic: boolean
}

type SetConfirmModalOpen = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_CONFIRM_MODAL_OPEN
  open: boolean
}

type Set2FaModalOpen = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_2_FA_MODAL_OPEN
  open: boolean
}

type CreateWithdrawalRequest = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.CREATE_WITHDRAWAL_REQUEST
  error: string
}

type SetVisitedGettingStarted = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_VISITED_GETTING_STARTED
  value: boolean
}

type SetCurrentMerchantAccountId = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_CURRENT_MERCHANT_ACCOUNT_ID
  currentMerchantAccountId: number
}

type SetIsHederaUIEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_HEDERA_UI_ENABLED
  isHederaUIEnabled: boolean
}

type SetIsPolygonUIEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_POLYGON_UI_ENABLED
  isPolygonUIEnabled: boolean
}

type SetIsAvalancheUIEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_AVALANCHE_UI_ENABLED
  isAvalancheUIEnabled: boolean
}

type SetIsTronUIEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_TRON_UI_ENABLED
  isTronUIEnabled: boolean
}

type SetIsBnbsmartUIEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_BNBSMART_UI_ENABLED
  isBnbsmartUIEnabled: boolean
}

type SetIsArbitrumUIEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_ARBITRUM_UI_ENABLED
  isArbitrumUIEnabled: boolean
}

type setIsUsdtBep20UIEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_USDT_BEP20_UI_ENABLED
  isUSDTBEP20UIEnabled: boolean
}

type SetIsRippleUIEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_RIPPLE_UI_ENABLED
  isRippleUIEnabled: boolean
}

type SetIsXsgdSplUIEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_XSGD_SPL_UI_ENABLED
  isXsgdSplUIEnabled: boolean
}

type SetIsXusdSplUIEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_XUSD_SPL_UI_ENABLED
  isXusdSplUIEnabled: boolean
}

type SetIsSolanaUIEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_SOLANA_UI_ENABLED
  isSolanaUIEnabled: boolean
}

type SetIsSsoTwoFaAuthenticatorEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_SSO_TWO_FA_AUTHENTICATED_ENABLED
  isSsoTwoFaAuthenticatorEnabled: boolean
}

type SetHasFetchingFeeError = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_HAS_FETCHING_FEE_ERROR
  hasError: boolean
}

type SetIsDptRiskModalApplied = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_DPT_RISK_MODAL_ENABLED
  isDptRiskModalEnabled: boolean
}

type SetIsFazzVaForDashboardEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_FAZZ_VA_FOR_DASHBOARD_ENABLED
  isFazzVaForDashboardEnabled: boolean
}

type SetIsTravelRuleDepositDeclarationEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_TRAVEL_RULE_DEPOSIT_DECLARATION_ENABLED
  isTravelRuleDepositDeclarationEnabled: boolean
}

type SetIsTravelRuleAddrWhitelistingEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_TRAVEL_RULE_ADDR_WHITELISTING_ENABLED
  isTravelRuleAddrWhitelistingEnabled: boolean
}

type SetIsRenewAddressAcknowledged = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_RENEW_ADDRESS_ACKNOWLEDGED
  isRenewAddressAcknowledged: boolean
}

type SetIsXusdCoachMarkActive = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_XUSD_COACHMARK_ACTIVE
  isXusdCoachmarkActive: boolean
}

type SetIsDashboardApprovalFlowUIEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_DASHBOARD_APPROVAL_FLOW_UI_ENABLED
  isDashboardApprovalFlowUIEnabled: boolean
}

type SetIsXusdErc20DepositEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_XUSD_ERC20_DEPOSIT_ENABLED
  isXusdErc20DepositEnabled: boolean
}

type SetIsXusdErc20WithdrawalEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_XUSD_ERC20_WITHDRAWAL_ENABLED
  isXusdErc20WithdrawalEnabled: boolean
}

type SetIsUsdtBep20DepositEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_USDT_BEP20_DEPOSIT_ENABLED
  isUsdtBep20DepositEnabled: boolean
}

type SetIsUsdtBep20WithdrawalEnabled = {
  type: typeof STRAITSX_LANDING_PAGE_ACTIONS.SET_IS_USDT_BEP20_WITHDRAWAL_ENABLED
  isUsdtBep20WithdrawalEnabled: boolean
}

export type ActionTypes =
  | SetIsZrc2DepositEnabled
  | SetIsXsgdErc20DepositEnabled
  | SetIsXsgdHtsDepositEnabled
  | SetIsXsgdMaticDepositEnabled
  | SetIsXsgdAvaxDepositEnabled
  | SetIsXsgdArbDepositEnabled
  | SetIsXsgdXrpDepositEnabled
  | SetIsUsdtTrc20DepositEnabled
  | SetIsXusdBep20DepositEnabled
  | SetIsXsgdZrc2WithdrawalEnabled
  | SetIsXsgdErc20WithdrawalEnabled
  | SetIsXsgdHtsWithdrawalEnabled
  | SetIsXsgdMaticWithdrawalEnabled
  | SetIsXsgdAvaxWithdrawalEnabled
  | SetIsXsgdArbWithdrawalEnabled
  | SetIsXsgdXrpWithdrawalEnabled
  | SetIsUsdtTrc20WithdrawalEnabled
  | SetIsXusdBep20WithdrawalEnabled
  | SetIsUsdcMaticDepositEnabled
  | SetIsUsdcMaticWithdrawalEnabled
  | SetIsZrc2NoticeBannerEnabled
  | SetIsDashboardBannerManagementEnabled
  | SetDashboardBanners
  | SetTab
  | SetTransferMethod
  | SetSelectedCurrency
  | SetSelectedBlockchain
  | SetSelectedBlockchainAddress
  | SetBankTransferNetwork
  | SetSelectedBankAccount
  | SetBankRoutingCode
  | SetBankIntermediarySwiftCode
  | SetWithdrawAmount
  | SetTransferRemark
  | SetWithdrawWalletId
  | SetIsStablecoinFeeLoading
  | SetStablecoinFee
  | SetFiatFee
  | SetIsHtsWithdrawalFeeCharging
  | SetIsWithdrawalAutomatic
  | SetConfirmModalOpen
  | Set2FaModalOpen
  | CreateWithdrawalRequest
  | SetVisitedGettingStarted
  | SetCurrentMerchantAccountId
  | SetIsHederaUIEnabled
  | SetIsPolygonUIEnabled
  | SetIsAvalancheUIEnabled
  | SetIsTronUIEnabled
  | SetIsBnbsmartUIEnabled
  | SetIsArbitrumUIEnabled
  | SetIsRippleUIEnabled
  | SetIsXsgdSplUIEnabled
  | SetIsXusdSplUIEnabled
  | SetIsSolanaUIEnabled
  | SetIsSsoTwoFaAuthenticatorEnabled
  | SetHasFetchingFeeError
  | SetIsDptRiskModalApplied
  | SetIsFazzVaForDashboardEnabled
  | SetIsTravelRuleDepositDeclarationEnabled
  | SetIsTravelRuleAddrWhitelistingEnabled
  | SetIsRenewAddressAcknowledged
  | SetIsXusdCoachMarkActive
  | SetIsDashboardApprovalFlowUIEnabled
  | SetIsXusdErc20DepositEnabled
  | SetIsXusdErc20WithdrawalEnabled
  | SetIsUsdtBep20DepositEnabled
  | SetIsUsdtBep20WithdrawalEnabled
  | setIsUsdtBep20UIEnabled
